/**
 * DuplicateLocationDialog - Confirmation dialog when adding a stop near an existing location
 *
 * Shows when user tries to add a manual stop within ~80 meters of an existing stop.
 * Allows user to merge with existing stop, create separate, or cancel.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MapPin, Building2, Merge } from 'lucide-react';
import { formatProximityDistance } from '../utils/routeOptimization';

export type DuplicateLocationChoice = 'merge' | 'separate' | 'cancel';

export interface NearbyStopInfo {
  dealer_name: string;
  dealer_address?: string;
  stop_type?: 'recall' | 'manual';
  distanceMiles: number;
}

interface DuplicateLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nearbyStop: NearbyStopInfo;
  newStopName: string;
  onChoice: (choice: DuplicateLocationChoice) => void;
}

/**
 * Dialog shown when user adds a manual stop near an existing stop location.
 * Offers three options: merge with existing, create separate, or cancel.
 */
export const DuplicateLocationDialog: React.FC<DuplicateLocationDialogProps> = ({
  open,
  onOpenChange,
  nearbyStop,
  newStopName,
  onChoice
}) => {
  const handleChoice = (choice: DuplicateLocationChoice) => {
    onChoice(choice);
    onOpenChange(false);
  };

  const stopTypeLabel = nearbyStop.stop_type === 'manual' ? 'manual stop' : 'recall stop';
  const distanceText = formatProximityDistance(nearbyStop.distanceMiles);
  const isSameLocation = nearbyStop.distanceMiles < 0.01;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <div className='flex items-center gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30'>
              <MapPin className='h-5 w-5 text-amber-600 dark:text-amber-400' />
            </div>
            <DialogTitle>Stop Already Exists Nearby</DialogTitle>
          </div>
          <DialogDescription className='pt-3 space-y-3'>
            <p>
              A {stopTypeLabel} already exists{' '}
              {isSameLocation ? (
                <>
                  at the <strong>same location</strong>.
                </>
              ) : (
                <>
                  <strong>{distanceText}</strong> from this location.
                </>
              )}
            </p>

            <div className='flex items-start gap-3 p-3 bg-muted rounded-lg'>
              <Building2 className='h-5 w-5 text-muted-foreground mt-0.5 shrink-0' />
              <div>
                <p className='font-medium text-foreground'>{nearbyStop.dealer_name}</p>
                {nearbyStop.dealer_address && (
                  <p className='text-sm text-muted-foreground'>{nearbyStop.dealer_address}</p>
                )}
              </div>
            </div>

            <p>
              Would you like to add <strong>{newStopName}</strong> to the existing stop, or create a
              separate stop?
            </p>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className='flex-col sm:flex-row gap-2'>
          <Button
            type='button'
            variant='outline'
            onClick={() => handleChoice('cancel')}
            className='sm:order-1'
          >
            Cancel
          </Button>
          <Button
            type='button'
            variant='secondary'
            onClick={() => handleChoice('separate')}
            className='sm:order-2'
          >
            Create Separate Stop
          </Button>
          <Button
            type='button'
            onClick={() => handleChoice('merge')}
            className='sm:order-3 bg-orange-600 hover:bg-orange-700'
          >
            <Merge className='h-4 w-4 mr-2' />
            Add to Existing Stop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateLocationDialog;
